import React, { FunctionComponent, useContext, useCallback } from 'react';
import { PageHeader, Form, Group, ResponsivePadding } from '../../components';
import { PaymentsFields } from './PaymentsFields';
import {
  useToaster,
  useAuthToken,
  useNextApplicationPath,
  useWriteableFieldFormValidation,
  useRedirectToNextApplicationPathIfPageIsHidden,
} from '../../hooks';
import {
  RegistrationStore,
  updateRegistration,
  BrandStore,
} from '../../context';
import { PaymentsFieldsValues } from './PaymentsFields.types';
import { history } from '../../history';
import { coreapi } from '../../api';
import {
  mapFormValuesToPayload,
  mapRegistrationToInitialFormValues,
  validateFormValues,
  formMutators,
} from './PaymentsFields.util';
import { PrimaryButton } from '@stax-core/truffle-components';
import { Prompt } from 'react-router-dom';
export const PaymentsPage: FunctionComponent = () => {
  const { toaster, toast } = useToaster();
  const { authToken } = useAuthToken();
  const nextPath = useNextApplicationPath();
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const {
    state: { brand },
  } = useContext(BrandStore);

  const { regPaymentsHeader, regPaymentsBody } = brand || {};

  const onSubmit = useCallback(
    async (formValues: PaymentsFieldsValues) => {
      const payload = mapFormValuesToPayload(formValues);

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        history.push(nextPath!);
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster, nextPath],
  );

  const validate = useWriteableFieldFormValidation(validateFormValues);

  useRedirectToNextApplicationPathIfPageIsHidden('Payments');

  return (
    <>
      <PageHeader
        header={regPaymentsHeader}
        id="payments-page-header"
        data-testid="payments-page-header"
        description={<p>{regPaymentsBody}</p>}
      />
      <Form
        initialValues={mapRegistrationToInitialFormValues(registration)}
        validate={validate}
        mutators={formMutators}
        onSubmit={onSubmit}
      >
        {formProps => (
          <PaymentsFields mode="edit" {...formProps}>
            <Prompt
              when={formProps.dirty && !formProps.submitting}
              message="You have unsaved changes; are you sure you want to leave without saving?"
            />
            <ResponsivePadding>
              <Group justify="end">
                <PrimaryButton
                  style={{ width: '100%' }}
                  type="submit"
                  id="payments-continue-button"
                  data-testid="payments-continue-button"
                  trackingId="payments-continue-button"
                  disabled={formProps.submitting}
                >
                  <span>
                    {formProps.submitting ? 'Saving...' : 'Save & Continue'}
                  </span>
                </PrimaryButton>
              </Group>
            </ResponsivePadding>
          </PaymentsFields>
        )}
      </Form>
    </>
  );
};
